import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { backdrop } from "./motion";
import { Play, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { useScrollLock } from "./useScrollLock";

/* Ported from the template's components/ui/promo-tour.tsx (d28b178,
   ee8695e). Self-hosted so nothing loads until someone presses play; the
   poster is about 140 KB, the video 9 MB (v6, 1080p at about 1.6 Mbps,
   2026-10-16; keep it near that so it plays smoothly on ordinary
   connections). */
const TOUR_VIDEO = "/promo/hyperclapper-tour.mp4";
const TOUR_POSTER = "/promo/hyperclapper-tour.jpg";

/**
 * Owns the promo tour dialog. Each surface renders its own trigger through
 * the render prop, so the player itself lives in one place. Stacked above the
 * auth shell (z-[80]) and the app's dialogs (z-[100]).
 */
export function PromoTour({ children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {children(() => setOpen(true))}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              {...backdrop(0.6)}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[110] flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                transition={{ type: "spring", stiffness: 300, damping: 28 }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={t("tour.title", "See HyperClapper in action")}
                className="w-full max-w-4xl overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] text-foreground shadow-[var(--shadow-lg)]"
              >
                <div className="flex items-center justify-between gap-4 border-b border-[var(--border)] px-5 py-3.5">
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold">{t("tour.title", "See HyperClapper in action")}</p>
                    <p className="truncate text-[12px] text-muted">
                      {t("tour.sub", "From pasting a post to watching it land, in 45 seconds")}
                    </p>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-[var(--surface-2)] hover:text-foreground"
                    aria-label={t("common.close", "Close")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <video
                  src={TOUR_VIDEO}
                  poster={TOUR_POSTER}
                  controls
                  autoPlay
                  playsInline
                  className="block aspect-video w-full bg-black"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

/** Poster frame with a play disc: the "small preview" trigger. */
export function TourThumb({ className }) {
  return (
    <span className={cn("relative block aspect-video overflow-hidden rounded-lg", className)}>
      <img src={TOUR_POSTER} alt="" className="h-full w-full object-cover" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/5">
        <span className="flex aspect-square h-[38%] max-h-14 min-h-6 items-center justify-center rounded-full bg-white/95 text-[var(--accent)] shadow-md transition-transform group-hover:scale-110">
          <Play className="h-1/2 w-1/2 translate-x-[6%]" fill="currentColor" strokeWidth={0} />
        </span>
      </span>
    </span>
  );
}
